import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef
} from 'react';
import { useFormikContext } from 'formik';
import { useStore } from 'components/Store';
import usePopulateMapData from 'hooks/usePopulateMapData';
import { FETCH_TYPES } from 'helpers/constants';
import { PlanWizardFormValues } from '../types';

interface PropertyTreeContextValue {
  propertyLandingPageData: any;
  fieldsAndAreasGeoJSONCollection: any;
  loading: boolean;
}

const PropertyTreeContext = createContext<PropertyTreeContextValue>({
  propertyLandingPageData: undefined,
  fieldsAndAreasGeoJSONCollection: undefined,
  loading: false
});

/**
 * Fetches the org's property tree (`usePopulateMapData`) once, as soon as
 * the Plan wizard mounts — not when the user navigates to the Properties
 * step. `ProgressStepper` only ever mounts the *active* step's component,
 * so triggering this fetch from inside `PropertiesStep` itself only starts
 * it the instant the user arrives there, leaving the crop-zone table
 * showing blank Crop/Farm columns for however long the fetch takes.
 *
 * Wrapping the whole wizard (`PlanStepsLayout`) instead starts the fetch
 * alongside the Details step, giving it the entire time the user spends
 * there to finish in the background — by the time they reach Properties,
 * `useResolveStubCropZones` usually finds the tree already loaded.
 */
export const PropertyTreeProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { values } = useFormikContext<PlanWizardFormValues>();
  const {
    state: { cropSeasons }
  } = useStore();
  const {
    dataSorted: propertyLandingPageData,
    fieldsAndAreasGeoJSONCollection,
    loading,
    reloadData
  } = usePopulateMapData({
    fetchType: FETCH_TYPES.propertiesMap,
    cropSeasonsId: undefined
  });

  const hasFetchedRef = useRef(false);
  const needsResolution = (values.cropZones ?? []).some(zone => !zone.name);

  useEffect(() => {
    // Editing an existing Plan can load crop zones saved under a crop
    // season other than whichever one(s) are currently globally selected —
    // `reloadData()` with no arguments falls back to that global selection
    // (`usePopulateMapData`'s `initialSettings` reads `selectedCropSeasons`
    // straight off localStorage), so a crop zone from any other season
    // would never resolve a name at all (same root cause fixed in
    // `PlanPropertiesTab.tsx`'s read-only view). Passing every org crop
    // season's id here instead covers the whole org.
    if (
      hasFetchedRef.current ||
      !needsResolution ||
      !(cropSeasons as Array<{ id: string }> | undefined)?.length
    ) {
      return;
    }
    hasFetchedRef.current = true;
    reloadData({
      seasonIds: (cropSeasons as Array<{ id: string }>).map(season => season.id)
    });
  }, [needsResolution, cropSeasons, reloadData]);

  const value = useMemo(
    () => ({
      propertyLandingPageData,
      fieldsAndAreasGeoJSONCollection,
      loading
    }),
    [propertyLandingPageData, fieldsAndAreasGeoJSONCollection, loading]
  );

  return (
    <PropertyTreeContext.Provider value={value}>
      {children}
    </PropertyTreeContext.Provider>
  );
};

export const usePropertyTreeContext = (): PropertyTreeContextValue =>
  useContext(PropertyTreeContext);
